
  import { defineComponent, computed, ref, watch, PropType } from 'vue';
  import type {
    ExpertReviewAreaDetailsObject,
    ExpertReviewAreaObject,
    ExpertReviewClusterDealObject,
    ExpertReviewQuarterObject,
    ExpertReviewRejectReasonObject,
  } from '@/store/modules/Indices/ExpertReviewModule';
  import {
    deltaClass,
    fmt,
    livePrice,
    liveQoq,
    pct,
    propertyTypeKey,
    sparkBars,
    useExpertReviewI18n,
  } from '@/core/helpers/expert-review';

  const NOTE_REASON_SLUG = 'other';
  const OUTLIER_FLOOR = 2000;
  const OUTLIER_NOTE_FLOOR = 2500;

  export default defineComponent({
    name: 'expert-review-drawer',
    props: {
      row: {
        type: Object as PropType<
          | (ExpertReviewAreaObject &
              Omit<ExpertReviewAreaDetailsObject, 'clusterDeals' | 'area'>)
          | null
        >,
        default: null,
      },
      clusterDeals: {
        type: Array as PropType<Array<ExpertReviewClusterDealObject>>,
        default: () => [],
      },
      quarter: {
        type: Object as PropType<ExpertReviewQuarterObject>,
        required: true,
      },
      reasons: {
        type: Array as PropType<Array<ExpertReviewRejectReasonObject>>,
        default: () => [],
      },
      quarterLabel: { type: String, default: '' },
      detailsStatus: { type: String, default: 'loading' },
      reasonsStatus: { type: String, default: 'loading' },
      open: { type: Boolean, default: false },
      canUpdate: { type: Boolean, default: false },
    },
    emits: ['close', 'approve', 'reject'],
    setup(props, { emit }) {
      const { translate, isArabic } = useExpertReviewI18n();

      const reasonsReady = computed(() => props.reasonsStatus === 'ready');
      const reasonsFailed = computed(() => props.reasonsStatus === 'error');
      const detailsReady = computed(() => props.detailsStatus === 'ready');
      const detailsFailed = computed(() => props.detailsStatus === 'error');

      const showReasons = ref(false);
      const selectedReason = ref<number | null>(null);
      const note = ref('');
      const expertValue = ref(0);
      const adjusting = ref(false);

      const hasExpertValue = computed(() => Number(expertValue.value) > 0);

      const needsNote = computed(
        () =>
          props.reasons.find((reason) => reason.id === selectedReason.value)
            ?.slug === NOTE_REASON_SLUG
      );

      const isCarried = computed(
        () => props.row?.evidence === 'CLUSTER_CARRIED'
      );
      const bars = computed(() =>
        props.row ? sparkBars(props.row.priceHistory) : []
      );
      const topDealPrice = computed(() =>
        props.clusterDeals.reduce(
          (top, item) => Math.max(top, item.pricePerSqm),
          0
        )
      );
      const outlierPrice = computed(() =>
        topDealPrice.value > OUTLIER_FLOOR ? topDealPrice.value : null
      );
      const showOutlierNote = computed(
        () => topDealPrice.value > OUTLIER_NOTE_FLOOR
      );

      watch([() => props.row?.id, () => props.row?.isFinal], () => {
        showReasons.value = false;
        selectedReason.value = props.reasons[0]?.id ?? null;
        note.value = '';
        adjusting.value = false;
        expertValue.value = Math.round(props.row?.previousPricePerSqm || 0);
      });

      // first press opens the reasons, second confirms
      const onReject = () => {
        if (!showReasons.value) {
          selectedReason.value = props.reasons[0]?.id ?? null;
          showReasons.value = true;
          if (props.row?.isFinal) adjusting.value = true;
          return;
        }
        if (selectedReason.value === null) return;
        if (needsNote.value && !note.value.trim()) return;
        if (adjusting.value && !hasExpertValue.value) return;
        emit('reject', {
          rejectReasonId: selectedReason.value,
          rejectReasonNote: note.value.trim() || undefined,
          expertValue: adjusting.value ? Number(expertValue.value) : null,
        });
      };

      return {
        translate,
        isArabic,
        reasonsReady,
        reasonsFailed,
        detailsReady,
        detailsFailed,
        isCarried,
        bars,
        outlierPrice,
        showOutlierNote,
        showReasons,
        selectedReason,
        note,
        needsNote,
        expertValue,
        adjusting,
        hasExpertValue,
        onReject,
        fmt,
        livePrice,
        liveQoq,
        pct,
        deltaClass,
        propertyTypeKey,
      };
    },
  });
